import { runtimeConfig } from "../config";
import { currentUser } from "./session";

export type ResourceChangedDetail = {
  method: string;
  route: string;
  actorId: number;
};

export function installRealtimeSession(): () => void {
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let heartbeatTimer: number | null = null;
  let closed = false;
  let retry = 0;

  const clearHeartbeat = () => {
    if (heartbeatTimer !== null) {
      window.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const connect = () => {
    const token = currentUser()?.accessToken;
    if (closed || !token) return;
    const url = new URL(runtimeConfig.realtimeOrigin);
    url.searchParams.set("token", token);
    socket = new WebSocket(url);
    socket.addEventListener("open", () => {
      retry = 0;
      clearHeartbeat();
      heartbeatTimer = window.setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "ping" }));
        }
      }, 25_000);
    });
    socket.addEventListener("message", (event) => {
      try {
        const message = JSON.parse(String(event.data)) as {
          type?: string;
          data?: ResourceChangedDetail;
        };
        if (message.type === "resource:changed" && message.data) {
          window.dispatchEvent(
            new CustomEvent<ResourceChangedDetail>("mdt:resource-changed", {
              detail: message.data,
            }),
          );
        }
      } catch {
        // Ignore malformed frames. The next valid server frame remains usable.
      }
    });
    socket.addEventListener("close", () => {
      clearHeartbeat();
      socket = null;
      if (closed) return;
      retry += 1;
      reconnectTimer = window.setTimeout(
        connect,
        Math.min(15_000, 500 * 2 ** retry),
      );
    });
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    clearHeartbeat();
    socket?.close();
  };
}
